import cn from "classnames";
import Image from "next/image";
import { useEffect, useState, memo } from "react";

import type { StrapiCollectionType, StrapiComponentType } from "@/src/type";

import styles from "./Messages.module.scss";

interface MessageBubbleProps {
  text: string;
}

function MessageBubble({ text }: MessageBubbleProps) {
  const [hidden, setHidden] = useState<boolean>(false);

  return (
    <div className={cn(styles.messageBubble, hidden ? styles.hidden : undefined)}>
      <div
        className={styles.messageBubbleDismiss}
        onClick={() => setHidden(true)}
      >
        <Image
          src="/image/icon_message_bubble_exit.svg"
          width={19}
          height={19}
          alt="Dismiss message bubble"
        />
      </div>
      <p>{text}</p>
    </div>
  );
}

interface MessageProps {
  promo?: StrapiCollectionType.Promo | null;
}

function Messages({ promo }: MessageProps) {
  const [messages, setMessages] = useState<any[]>([]);
  const [renderedMessages, setRenderMessages] = useState<number[]>([]);
  const profileBanner = promo?.promoControl?.[0] as StrapiComponentType.Object.ProfileBanner;

  /**
   * when messages are passed render the first one
   */
  useEffect(() => {
    setMessages([]);

    let timeoutHolder: any = undefined;
    for (const data of profileBanner?.messages ?? []) {
      const delay = (data.delaySeconds ?? 60) * 1000;
      timeoutHolder = setTimeout(() => {
        setMessages([{ text: data.message, id: data.id }]);
        setRenderMessages([data.id]);
      }, delay);
      break;
    }

    return () => {
      if (timeoutHolder) {
        clearTimeout(timeoutHolder);
      }
    };
  }, [profileBanner?.messages]);

  /**
   * after the last message is rendered, set the timeout for the next message
   */
  useEffect(() => {
    if ((messages ?? []).length === 0) {
      return;
    }

    let timeoutHolder: any = undefined;
    for (const data of profileBanner?.messages ?? []) {
      if (!renderedMessages.includes(data.id)) {
        const newRenderedMessages = JSON.parse(JSON.stringify(renderedMessages));
        newRenderedMessages.push(data.id);

        const newMessages = JSON.parse(JSON.stringify(messages));
        newMessages.push({ text: data.message, id: data.id });

        const delay = (data.delaySeconds ?? 60) * 1000;
        timeoutHolder = setTimeout(() => {
          setMessages(newMessages);
          setRenderMessages(newRenderedMessages);
        }, delay);
        break;
      }
    }

    return () => {
      if (timeoutHolder) {
        clearTimeout(timeoutHolder);
      }
    };
  }, [renderedMessages, messages, profileBanner?.messages]);

  if ((profileBanner?.messages ?? []).length === 0) {
    return <></>;
  }

  return (
    <div className={styles.messages}>
      {messages.map((opt) => (
        <MessageBubble
          key={opt.id}
          text={opt.text}
        />
      ))}
    </div>
  );
}

export default memo(Messages);

/*
//DEMO
setTimeout(() => {
  setMessage([{ text: "Own seamlessly in Flow with pricing starting as low as $400k." }]);
}, 3000);

setTimeout(() => {
  setMessage([
    { text: "Own seamlessly in Flow with pricing starting as low as $400k." },
    { text: "Explore spaces here" },
  ]);
}, 6000);

setTimeout(() => {
  setMessage([
    { text: "Own seamlessly in Flow with pricing starting as low as $400k." },
    { text: "Explore spaces here" },
    { text: "Call me at 1-800-393-3020" },
  ]);
}, 10000);
*/
